'use client';
import { notFound, useParams } from 'next/navigation';

import { useInView } from 'react-intersection-observer';

import { Spinner } from '@uikit';

import { usePublicUserQuery, useUserClaimedHotOffersInfiniteQuery } from '@query';

import PageHeader from '@store:web/components/layout/PageHeader/PageHeader';

import UserHotOfferCard from '../UserPage/UserHotOfferCard';

const UserHotOffersPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { data: user } = usePublicUserQuery({ searchParams: { id: userId } });

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useUserClaimedHotOffersInfiniteQuery(
      { userId },
      {
        enabled: !!user,
      },
    );
  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const allOffers = data?.pages.flatMap((page) => page);

  const showContent = !isLoading && !!allOffers?.length;
  const showLoader = isLoading || hasNextPage;
  const showEmpty = !isLoading && !allOffers?.length;

  if (!user) {
    return notFound();
  }

  return (
    <>
      <PageHeader
        page="user"
        disableBanner
        extendBreadcrumbs={[
          {
            label: `@${user.attributes.magicId}`,
            pathname: `/user/${user.attributes.magicId}`,
          },
          {
            label: `Claimed Hot Offers`,
            pathname: `/user/${user.attributes.magicId}/claimed-hot-offers`,
          },
        ]}
      />
      <main className="v2-container">
        <div className="md:rounded md:pt-6 md:px-6 md:pb-[54px] md:shadow-main">
          <h1 className="font-bold md:t-h3 mb-4 md:mb-10">Claimed Hot Offers</h1>

          <div className="flex gap-2">
            {showContent && (
              <div className="grid md:grid-cols-2 gap-2 md:gap-x-8 md:gap-y-6 w-full">
                {allOffers?.map((offer) => (
                  <UserHotOfferCard offer={offer} key={offer.id} />
                ))}
              </div>
            )}

            {showEmpty && (
              <div className="w-full py-8">
                <h3 className="t-h4 text-center text-neutral-300">Empty!</h3>
              </div>
            )}

            {showLoader && (
              <Spinner
                key={data?.pages.length}
                ref={ref}
                className="self-center justify-self-center m-auto"
                size="md"
              />
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default UserHotOffersPage;
